import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useVideo } from '../context/VideoContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiBriefcase, FiLayers, FiMaximize2, FiBarChart2 } = FiIcons;

const formatCost = (cost) => {
  if (cost === null || cost === undefined || cost === '') return null;
  const asString = String(cost).trim();
  const asNumber = Number(asString.replace(/[^0-9.]/g, ''));
  if (!isNaN(asNumber) && asNumber > 0) {
    return '$' + asNumber.toLocaleString();
  }
  return asString;
};

const VideoCard = ({ video, index }) => {
  const { incrementViews, activeVideoId, setActiveVideoId } = useVideo();
  const navigate = useNavigate();
  const [thumbError, setThumbError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const isPlaying = activeVideoId === video.id;

  const getVideoInfo = (url) => {
    if (!url) return null;
    const youtubeRegex = new RegExp('(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})', 'i');
    const vimeoRegex = new RegExp('(?:vimeo\\.com/|player\\.vimeo\\.com/video/)([0-9]+)', 'i');
    const ytMatch = url.match(youtubeRegex);
    if (ytMatch) return { type: 'youtube', id: ytMatch[1] };
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
    return null;
  };

  const getThumbnailUrl = (url) => {
    const info = getVideoInfo(url);
    if (!info) return null;
    if (info.type === 'youtube') return `https://img.youtube.com/vi/${info.id}/maxresdefault.jpg`;
    if (info.type === 'vimeo') return `https://vumbnail.com/${info.id}.jpg`;
    return null;
  };

  const getEmbedUrl = (url) => {
    const info = getVideoInfo(url);
    if (!info) return url;
    if (info.type === 'youtube') return `https://www.youtube.com/embed/${info.id}?autoplay=1&rel=0&modestbranding=1&iv_load_policy=3`;
    if (info.type === 'vimeo') return `https://player.vimeo.com/${info.id}?autoplay=1&dnt=1`;
    return url;
  };

  const handleThumbnailClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPlaying) {
      incrementViews(video.id);
      setActiveVideoId(video.id);
    }
  };

  const handleCardClick = () => {
    if (isPlaying) return;
    const urlSlug = video.slug || video.id;
    navigate(`/project/${urlSlug}`);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    const urlSlug = video.slug || video.id;
    navigate(`/project/${urlSlug}`);
  };

  const renderStyles = () => {
    const styles = Array.isArray(video.visual_style) ? video.visual_style : [video.visual_style];
    return styles.filter(Boolean).join(' • ');
  };

  const renderTypes = () => {
    const types = Array.isArray(video.video_type) ? video.video_type : [video.video_type];
    return types.filter(Boolean).join(' • ');
  };

  const thumbnailUrl = getThumbnailUrl(video.video_link);
  const formattedCost = formatCost(video.estimated_cost);

  return (
    <motion.div
      className="glass-card p-4 group relative border-white/5 overflow-hidden cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
      whileHover={{ y: -8 }}
      onClick={handleCardClick}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/10 blur-3xl -z-10" />

      {/* Thumbnail/Player Section */}
      <div
        className="relative aspect-video rounded-xl overflow-hidden mb-5 bg-zinc-950 group-hover:shadow-[0_0_30px_rgba(255,107,53,0.15)] transition-shadow"
        onClick={handleThumbnailClick}
      >
        <AnimatePresence mode="wait">
          {!isPlaying ? (
            <motion.div
              key="thumbnail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              {thumbnailUrl && !thumbError && !imgLoaded && (
                <div className="absolute inset-0 bg-white/5 animate-pulse z-10 flex items-center justify-center">
                  <div className="w-10 h-10 border-2 border-white/10 border-t-white/30 rounded-full animate-spin" />
                </div>
              )}

              {thumbnailUrl && !thumbError ? (
                <img
                  src={thumbnailUrl}
                  alt={video.title}
                  className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImgLoaded(true)}
                  onError={() => { setThumbError(true); setImgLoaded(true); }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-zinc-900" />
              )}

              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors z-10" />
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <motion.div
                  className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.1 }}
                >
                  <SafeIcon icon={FiPlay} className="text-white text-xl translate-x-0.5" />
                </motion.div>
              </div>
              <div className="absolute top-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full border border-white/10 text-[10px] font-bold uppercase tracking-wider text-orange-400 z-30">
                {renderTypes()} • {video.industry}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="player"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 z-20"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={getEmbedUrl(video.video_link)}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.title}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors line-clamp-1">{video.title}</h3>
            <div className="flex items-center gap-1.5 mt-1 text-slate-500">
              <SafeIcon icon={FiLayers} className="text-[10px] text-orange-500/50" />
              <span className="text-[11px] font-medium uppercase tracking-wider">{renderStyles()}</span>
            </div>
          </div>
          <div
            className="p-2 rounded-lg bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/10"
            onClick={handleViewDetails}
          >
            <SafeIcon icon={FiMaximize2} className="text-white/40 group-hover:text-orange-400" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 py-3 border-y border-white/5">
          <div className="flex items-center gap-2 text-slate-400">
            <SafeIcon icon={FiBriefcase} className="text-xs text-orange-500/50" />
            <span className="text-[11px] font-medium truncate">{video.client || 'Portfolio'}</span>
          </div>
          <div className="flex items-center justify-end">
            {formattedCost && (
              <span className="text-sm font-medium text-gray-300">{formattedCost}</span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <a
            href="https://www.whatastory.agency/resources/ai-quote?source=videoexamples.com&button=videocard"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 text-xs bg-white/5 group-hover:bg-orange-500/10 group-hover:border-orange-500/30 border border-transparent px-3 py-1.5 rounded-lg transition-all text-slate-400 group-hover:text-orange-400 group-hover:font-bold"
          >
            <SafeIcon icon={FiBarChart2} className="text-xs group-hover:text-orange-400" />
            Analyze Pricing
          </a>
          <div
            className="text-[10px] font-black text-white/20 uppercase tracking-widest group-hover:text-white/50 transition-colors cursor-pointer hover:underline"
            onClick={handleViewDetails}
          >
            View Details
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const VideoCardSkeleton = () => (
  <div className="glass-card p-4 border-white/5 overflow-hidden">
    <div className="aspect-video rounded-xl bg-white/5 animate-pulse mb-5 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
    </div>
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 space-y-2">
          <div className="h-6 bg-white/5 rounded w-3/4 animate-pulse relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
          </div>
          <div className="h-3 bg-white/5 rounded w-1/2 animate-pulse" />
        </div>
        <div className="w-8 h-8 bg-white/5 rounded-lg animate-pulse" />
      </div>
      <div className="grid grid-cols-2 gap-4 py-3 border-y border-white/5">
        <div className="h-4 bg-white/5 rounded w-2/3 animate-pulse" />
        <div className="h-4 bg-white/5 rounded w-2/3 ml-auto animate-pulse" />
      </div>
      <div className="flex justify-between pt-1">
        <div className="h-4 bg-white/5 rounded w-1/3 animate-pulse" />
        <div className="h-3 bg-white/5 rounded w-1/4 animate-pulse" />
      </div>
    </div>
  </div>
);

export default VideoCard;